/**
 * Accessible series tabs for ADO homepage category product grids.
 * Keyboard: Arrow / Home / End; roving tabindex on the tablist.
 * Inactive panels may defer markup in <template class="ado-category-tabs__deferred">.
 */
class AdoCategoryTabs extends HTMLElement {
  /** @type {HTMLElement[]} */
  #tabs = [];
  /** @type {HTMLElement[]} */
  #panels = [];

  connectedCallback() {
    this.#tabs = Array.from(this.querySelectorAll('[role="tab"]'));
    this.#panels = Array.from(this.querySelectorAll('[role="tabpanel"]'));
    if (!this.#tabs.length) return;

    this.#tabs.forEach((tab, index) => {
      tab.addEventListener('click', () => this.#select(index));
      tab.addEventListener('keydown', (event) => this.#onKeydown(event, index));
    });
  }

  /**
   * @param {HTMLElement} panel
   */
  #hydrateDeferred(panel) {
    const deferred = panel.querySelector(':scope > template.ado-category-tabs__deferred');
    if (!deferred) return;
    panel.appendChild(deferred.content.cloneNode(true));
    deferred.remove();
  }

  /**
   * @param {number} index
   * @param {{ focus?: boolean }} [options]
   */
  #select(index, options = {}) {
    const { focus = true } = options;
    this.#tabs.forEach((tab, i) => {
      const selected = i === index;
      tab.setAttribute('aria-selected', selected ? 'true' : 'false');
      tab.tabIndex = selected ? 0 : -1;

      const panel = this.#panels[i];
      if (!panel) return;
      if (selected) {
        this.#hydrateDeferred(panel);
        panel.removeAttribute('hidden');
        panel.setAttribute('tabindex', '0');
      } else {
        panel.setAttribute('hidden', '');
        panel.removeAttribute('tabindex');
      }
    });

    if (focus) this.#tabs[index]?.focus();
  }

  /**
   * @param {KeyboardEvent} event
   * @param {number} index
   */
  #onKeydown(event, index) {
    const count = this.#tabs.length;
    let next = null;

    switch (event.key) {
      case 'ArrowRight':
      case 'ArrowDown':
        next = (index + 1) % count;
        break;
      case 'ArrowLeft':
      case 'ArrowUp':
        next = (index - 1 + count) % count;
        break;
      case 'Home':
        next = 0;
        break;
      case 'End':
        next = count - 1;
        break;
      default:
        return;
    }

    event.preventDefault();
    this.#select(next);
  }
}

if (!customElements.get('ado-category-tabs')) {
  customElements.define('ado-category-tabs', AdoCategoryTabs);
}
